import {
  ID_NAME_MAP,
  TOGGLE_MAP_TABLE,
  TOGGLE_MAP_CARD,
  COMPARATOR_MAP_FUNCTION,
  COMPARISON,
  COMPARISONS_OPERAND_OPTIONS_ID,
  DEFAULT_PERVIEW_VALUE,
  EQUAL,
  FAILURE,
  GREATER,
  KPI_REVIEW_CONFIG_NAME,
  LESS,
  SUCCESS,
  THRESHOLD,
  ALLOWED_KPI_CONFIG_OPTIONS,
  TABLE_DUMMY_DATA,
  SUMMARY_CARD_DUMMY_DATA,
  CARD_PREVIEW_DUMMY_DATA,
  ID_TO_NAME_MAPPING,
  BUTTON_TABLE_MAP
} from "./manageKPIReviewRegistry";
import {
  validateMetaDataResponse,
} from "../../../../services/uiConfigService";
import { InputFieldLengthValidationType, KPIReviewType } from "./manageKPIType";
import { getLob } from "../../../../services/authenticationService";
import { validateWithOtp } from "../../../../stateManagement/actions/validateOtpPopupActions";
import { openPopup } from "../../../../stateManagement/actions/popupActions";
import { META_DATA_BATCH_API } from "../../../../services/networkService";
import { metaDataBatchPayload } from "../../../../types/types";

export const getTableButtonMap = (kpi_id : string , buttons : Record<string,any>, tableSummaries : Record<string,any>) : Record<string,any> => {
  // if BUTTON_TABLE_MAP does not have kpi_id key then return empty object else return a map of buttons->button.id : tableSummaries ( where BUTTON_TABLE_MAP[button.id] === tableSummaries->tableSummary.id)
  // console.log("kpi_id",kpi_id)
  // console.log("buttons",buttons)
  // console.log("tableSummaries",tableSummaries)
  const mappingForCurrentKpi = BUTTON_TABLE_MAP[kpi_id] || {};
  if(!mappingForCurrentKpi)
      return {};
  const tableButtonMap : Record<string,any> = {};
  buttons.forEach((button: Record<string,any>)=>{
    if(mappingForCurrentKpi[button.id]){
      tableButtonMap[button.id] = tableSummaries.find((tableSummary: Record<string,any>)=>tableSummary.id === mappingForCurrentKpi[button.id]);
    }
  });
  // 
  return tableButtonMap;

}

export const checkIfTitleIsUnique = (kpi: Record<string, any>, title: string): boolean => {
  // Check if the title is different from all data point labels
  return !kpi.data_points?.some((dp: Record<string, any>) => dp?.label?.trim() === title?.trim());
}

export const checkIfDataPointLabelIsUnique = (kpi: Record<string, any>, dp_id: string, label: string): boolean => {
  // Check if the label is different from the kpi.title
  if (kpi?.title?.trim()?.toLowerCase() === label?.trim()?.toLowerCase()) {
    return false; // The label is not unique if it matches the title
  }

  // Check if the label is different from all data points other than itself
  const isLabelUnique = !kpi.data_points?.some((dp: Record<string, any>) => 
    dp.id !== dp_id && dp?.label?.trim()?.toLowerCase() === label?.trim()?.toLowerCase()
  );

  return isLabelUnique; // Return true if the label is unique, otherwise false
}

export const checkForDataFieldValidation = (
  userConfigArray: Record<string, any>[],
  selectedKPIs: string[],
  inputLengthValidation:InputFieldLengthValidationType
): boolean => {
  // Validation 1: Check if selectedKPIs is not empty
  if (selectedKPIs?.length === 0) {
    return false; // Validation fails if selectedKPIs is empty
  }

  // Validation 2: Check userConfigArray for valid lengths
  for (const item of userConfigArray) {
    if (!selectedKPIs.includes(item.id)) {
      continue; // Skip to the next iteration if item.id does not exist in the selectedKPIs array
    }
    const titleLength = item?.title.trim()?.length;
    const maxLength = inputLengthValidation.max;
    const minLength = inputLengthValidation.min;
    // Check if title length is valid
    if (titleLength < minLength || titleLength > maxLength) {
      return false; // Validation fails if title length is invalid
    }

    // Check if datapoints exist and validate their labels
    if (Array.isArray(item.data_points)) {
      for (const dataPoint of removeValidationItemDataPoint(item.data_points)) {
        const labelLength = dataPoint?.label?.trim()?.length;

        // Check if label length is valid
        if (labelLength < minLength || labelLength > maxLength) {
          return false; // Validation fails if any datapoint label length is invalid
        }
      }
    }
    // Check if all input fields of kpi are unique
    if (!checkIfInputFieldsAreUnique(item)) {
      return false;
    }
  }

  return true; // All validations passed
};

export const checkIfInputFieldsAreUnique = (kpi: Record<string, any>): boolean => {
  const valuesToCheck = new Set();

  // Add title to valuesToCheck
  if (kpi.title) {
    valuesToCheck.add(kpi?.title?.trim()?.toLowerCase() || "");
  }

  // Add data points labels to valuesToCheck
  kpi.data_points?.forEach((dp: Record<string, any>) => {
    valuesToCheck.add(dp.label?.trim()?.toLowerCase() || "");
  });

  // Check uniqueness
  return valuesToCheck.size === (1 + (kpi.data_points?.length || 0));
}

export const getIdToNameMappingOfDataPointsFromKPIID = (kpi_id:string)=>{
  return ID_TO_NAME_MAPPING[kpi_id] || {};
}
export const prepareDataForSaving = (
  userConfig: Record<string, any>[],
  selectedKPIsKeys: string[],
  defautlConfigGlobal : Record<string,any>[]
): Record<string, any>[] => {
  const defaultConfig = getDefaultConfigArray(defautlConfigGlobal);

  const finalConfig: Record<string, any>[] = selectedKPIsKeys.map((kpi_id: string) => {
    const userConfigValue: Record<string, any> = userConfig.find((_kpi: Record<string, any>) => _kpi.id === kpi_id) || {};
    const defaultConfigValue: Record<string, any> = defaultConfig.find((_kpi: Record<string, any>) => _kpi.id === kpi_id) || {};

    // 1. Update the KPI title
    const customTitle = userConfigValue?.title || "";

    // 2. Update the data_points
    const customButtons = userConfigValue?.buttons || [];

    // 3. Update the detail_summary > buttons
    const customDetailSummary = defaultConfigValue?.detail_summary?.map((item: Record<string, any>) => {
      if (item.id === "toggleListView") {
        return { ...item, toggle_list: customButtons }; // Update toggle_list with custom buttons
      }
      return item; // Return the item unchanged
    }) || [];

    // Prepare the current KPI configuration
    const currentKPI = {
      ...defaultConfigValue,
      title: customTitle ?? '',
      data_points: userConfigValue?.data_points ?? [],
      detail_summary: customDetailSummary ?? [],
    };

    return currentKPI;
  });

  return finalConfig;
};
export const getPreviewCardDummyDatabyCardID = (card_id : string) => {
  return CARD_PREVIEW_DUMMY_DATA[card_id] || {}
}
export const getSummaryCardDummyDatabyCardID = (card_id : string) => {
  return SUMMARY_CARD_DUMMY_DATA[card_id] || {}
}
export const getTableDummyDataByTableID = (table_id: string) => {
  return TABLE_DUMMY_DATA[table_id] || {};
};
export const getDataFromConfig = (
  valueOf: string,
  id: string | string[],
  defaultConfigGlobal: Record<string,any>
) => {
  const configuration: Record<string, any>[] = getDefaultConfigArray(defaultConfigGlobal);
   if (valueOf === "KPI") {
    for (const item of configuration) {
      if (item.id === id) return item;
    }
    return {};
  }
  else if(valueOf === "KPI_datapoint_info"){
    for(const item of configuration){
      if(item.id===id[0]){
        for(const point of (item.data_points || [])){
          if(point.id === id[1]){
            return point|| {};
          }
        }
      }
    }
    return {};
  }
  else if (valueOf === "KPI_summary_card_data") {
    for (const item of configuration) {
      if (item.id === id)
        return (
          item.detail_summary?.filter(
            (summary_item: Record<string, any>) => summary_item.type === "card"
          ) || []
        );
    }
    return {};
  } else if (valueOf === "KPI_detail_summary_tables") {
    for (const item of configuration) {
      if (item.id === id)
        return (
          item.detail_summary?.filter(
            (summary_item: Record<string, any>) => summary_item.type === "table"
          ) || []
        );
    }
    return {};
  } else if (valueOf === "KPI_actionTabColor") {
    for (const item of configuration) {
      if (item.id === id) return item.actionTabColour || '#00C6B2';
    }
    return "#000000";
  } else if (valueOf === "KPI_tabSvgPath") {
    for (const item of configuration) {
      if (item.id === id) return item.tabSvgPath;
    }
    return "/home";
  }  else if (valueOf === "KPI_title") {
    for (const item of configuration) {
      if (item.id === id) {
        return item.title || "PLACEHOLDER TITLE";
      }
    }
    return "PLACEHOLDER TITLE";
  } else if (valueOf === "KPI_data_points") {
    for (const item of configuration) {
      if (item.id === id) {
        return item.data_points || [];
      }
    }
    return [];
  } else if (valueOf === "KPI_summary_buttons") {
    for (const item of configuration) {
      if (item.id === id) {
        if (item.detail_summary) {
          for (const summary of item.detail_summary) {
            if (summary.id = "toggleListView") {
              return summary.toggle_list || [];
            }
          }
        }
      }
    }
    return [];
  }

  return "";
};

export const removeValidationItemDataPoint = (
  config: Record<string, any>[] | any
): Record<string, any>[] => {
  return config?.filter(
    (item: Record<string, any>) => !item?.hasOwnProperty("validation")
  );
};

export const getDefaultConfigArray = (defaultConfigGlobal: Record<string, any>) => {
  const defaultConfigJSON: Record<string, any> = defaultConfigGlobal?.["kpiConfigList"] ?? {};
  const defaultConfig: Record<string, any>[] = formatDataWithShow(
    defaultConfigJSON?.["value"] || []
  );
  return defaultConfig;
};

export const getDefaultKPITitle = (id: string , defaultConfigGlobal : Record<string,any>): string => {
  const defautlKpisConfig : Record<string,any>[] = defaultConfigGlobal?.["kpiConfigList"]?.["value"] ?? [];
  return defautlKpisConfig.find(obj => obj["id"]=== id)?.["title"] ?? "PLACEHOLDER TITLE"
   // return DEFAULT_KPI_TITLE[id] || "PLACEHOLDER TITLE";
};
export const isKPIConfigAllowed = (id: string): boolean => {
  return ALLOWED_KPI_CONFIG_OPTIONS.includes(id);
};
export const getAllAllowedKPIsID = (defaultConfigGlobal: Record<string,any>) : string[] =>{
  const allKpisInDefault = defaultConfigGlobal?.["kpiConfigList"]?.["value"] ?? []
  return allKpisInDefault.map((kpi:Record<string,any>)=>kpi["id"]);
}



export function isValidHexCode(hexCode: string): boolean {
  //if (hexCode.length !== 7) return false;
  const hexRegex = /^#([0-9a-f]{3}){1,2}$/i;
  return hexRegex.test(hexCode);
}

export function formatDataWithShow(
  kpiReview: Record<string, any>[]
): KPIReviewType[] {
  let temp: Record<string, any>[] = [];
  let x: Record<string, any>[] = [];
  if(kpiReview.length === 0) return [];
  kpiReview.map((item: Record<string, any>) => {
    if ("data_points" in item) temp.push(item);
  });

  const filter = kpiReview.filter((item: Record<string, any>) => {
    if ("data_points" in item || "detail_summary" in item) return item;
  });
  filter.forEach((item: Record<string, any>) => {
    if (!("tabSvgPath" in item)) {
      item.tabSvgPath = "assets/svg/home.svg";
    }
    if ("tabSvgPath" in item && item.tabSvgPath === "") {
      item.tabSvgPath = "assets/svg/home.svg";
    }
    if ("data_points" in item) {
      item.data_points.forEach((point: Record<string, any>) => {
        if (!("show" in point)) {
          point.show = true;
        }
      });
    }

    if (item.detail_summary) {
      item.detail_summary.forEach((summary: Record<string, any>) => {
        if (!("show" in summary)) {
          summary.show = true;
        }

        if ("data_points" in summary) {
          summary.data_points.forEach((point: Record<string, any>) => {
            if (!("show" in point)) {
              point.show = true;
            }
          });
        }
        if ("toggle_list" in summary) {
          summary.toggle_list.forEach((point: Record<string, any>) => {
            if (!("show" in point)) {
              point.show = true;
            }
          });
        }
      });
    }
  });

  let finalData = [...filter];
  return finalData;
}

export function formatDataWithoutFasleShow(
  kpiReview: Record<string, any>
): KPIReviewType[] {
  let temp: Record<string, any>[] = [];
  let x: Record<string, any>[] = [];
  kpiReview.map((item: Record<string, any>) => {
    if ("data_points" in item) temp.push(item);
  });

  const filter = kpiReview.filter((item: Record<string, any>) => {
    if ("data_points" in item || "detail_summary" in item) return item;
  });

  filter.forEach((item: Record<string, any>) => {
    if ("data_points" in item) {
      item.data_points = item.data_points.filter(
        (point: Record<string, any>) => {
          if ("show" in point) {
            if (point.show === true) {
              delete point.show;
              return true;
            } else {
              return false;
            }
          }
          return true;
        }
      );
    }

    if (item.detail_summary) {
      item.detail_summary = item.detail_summary.filter(
        (summary: Record<string, any>) => {
          if (summary?.type === "card") {
            if ("show" in summary) {
              if (summary.show === true) {
                delete summary.show;
              } else {
                return false;
              }
            }
          }

          if ("data_points" in summary) {
            summary.data_points = summary.data_points.filter(
              (point: Record<string, any>) => {
                if ("show" in point) {
                  if (point.show === true) {
                    delete point.show;
                    return true;
                  } else {
                    return false;
                  }
                }
                return true;
              }
            );
          }

          if ("toggle_list" in summary) {
            summary.toggle_list = summary.toggle_list.filter(
              (point: Record<string, any>) => {
                if ("show" in point) {
                  if (point.show === true) {
                    delete point.show;
                    return true;
                  } else {
                    return false;
                  }
                }
                return true;
              }
            );
          }

          return true;
        }
      );
    }
  });

  let finalData = [...filter];
  return finalData;
}

export const getFormattedDate = () => {
  const date = new Date();
  const formattedDate = date.toLocaleDateString("default", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Function to add ordinal suffix to the day
  function addOrdinalSuffix(day: number) {
    if (day > 10 && day < 20) {
      return day + "th";
    } else {
      switch (day % 10) {
        case 1:
          return day + "st";
        case 2:
          return day + "nd";
        case 3:
          return day + "rd";
        default:
          return day + "th";
      }
    }
  }

  const [day, month, year] = formattedDate.split(" ");

  return "PTD " + addOrdinalSuffix(parseInt(day)) + " " + month + " " + year;
};


export const validateKPIReviewField = (
  id: string,
  val: Record<string, any>,
  preErrors: Record<string, string>,
  activeKeys?: string[]
) => {
  const err: any = { ...preErrors };

  if (id === "mainLabel") {
    err[id] =
      val.label === null || val.label.trim() === "" ? "Field is required" : "";
  }

  if (id == "tag") {
    err[id] = {
      label:
        val.label === null || val.label === undefined || val.label.trim() === ""
          ? "Field is Required"
          : "",
      color: isValidHexCode(val.color) ? "" : "Invalid Color",
    };
  } else if (id === "combined") {
    err[id] = {
      label:
        val.label === null || val.label === undefined || val.label.trim() === ""
          ? "Field is Required"
          : "",
      validation: validateValidationsFields(
        val.validation,
        val.unit,
        activeKeys
      ),
    };
  } else if (val.show) {
    err[id] = {
      label:
        val.label === null || val.label === undefined || val.label.trim() === ""
          ? "Field is Required"
          : "",
      validation: validateValidationsFields(
        val.validation,
        val.unit,
        activeKeys
      ),
    };
  }

  return err;
};

export const validateValidationsFields = (
  validation: any,
  unit: "number" | "percentage",
  activeKeys?: string[]
) => {
  const { threshold, comparison, type } = validation;

  if (type === THRESHOLD.value) {
    return {
      [THRESHOLD.value]: {
        value:
          threshold.value === null ||
          threshold.value === undefined ||
          threshold.value.toString().trim() === ""
            ? "Field is required"
            : unit === "number"
            ? Number(threshold.value) < 0
              ? "Value should be greater than zero"
              : ""
            : Number(threshold.value) < 0 || Number(threshold.value) > 100
            ? "Value should be between 0 - 100"
            : "",
        ec: isValidHexCode(threshold.ec) ? "" : "Invalid Color",
        lc: isValidHexCode(threshold.lc) ? "" : "Invalid Color",
        gc: isValidHexCode(threshold.gc) ? "" : "Invalid Color",
      },
    };
  } else if (type === COMPARISON.value) {
    return {
      [COMPARISON.value]: {
        operand1:
          comparison.operand1 === null ||
          comparison.operand1 === undefined ||
          comparison.operand1.toString().trim() === "" ||
          (activeKeys && !activeKeys?.includes(comparison.operand1))
            ? "Field is required"
            : "",
        operand2:
          comparison.operand2 === null ||
          comparison.operand2 === undefined ||
          comparison.operand2.toString().trim() === ""
            ? "Field is required"
            : "",
        operation:
          comparison.operation === null ||
          comparison.operation === undefined ||
          comparison.operation.toString().trim() === "" ||
          (activeKeys && !activeKeys?.includes(comparison.operand2))
            ? "Field is required"
            : "",
        color: isValidHexCode(comparison.color) ? "" : "Invalid Color",
      },
    };
  } else return {};
};

export const saveKPIReviewData = async (KPIReviews: Record<string, any>[]) => {
  // const formattedBody = getFormattedBody(KPIReviews);

  const configVal = [{
        name: "kpiConfigList",
        type: "list",
        value: KPIReviews,
        docUrl: "",
        description: "list of KPIs to be displayed in the app",
        defaultValue: [],
        possibleVals: ["Map[]"],
  }];

  const lob = getLob();
  
  const finalConfig: metaDataBatchPayload = {
    features: [
      {
        domainName: "clientconfig",
        domainType: KPI_REVIEW_CONFIG_NAME,
        domainValues: configVal,
        lob,
      },{
        domainName: "clientconfig",
        domainType: "salesrep_" + KPI_REVIEW_CONFIG_NAME,
        domainValues: configVal,
        lob,
      }
    ],
  };

  validateWithOtp(
    async (verifiedUser, verifyResponse?: any) => {
      try {
        if (verifyResponse.status >= 200 || verifyResponse.status < 300) {
          const validationObj = validateMetaDataResponse(
            verifyResponse?.data?.features
          );
          if (validationObj.success) {
            openPopup("Success", "Configuration Updated");
            // curConfigRef.current = { ...newConfigRef.current };
            // getCatalogueConfiguration();
          } else {
            openPopup("Error", validationObj.message);
          }
        } else {
          throw new Error("Something went wrong");
        }
      } catch (err) {
        if (err instanceof Error) openPopup("Error", err?.message);
      }
    },
    undefined,
    "admin",
    META_DATA_BATCH_API,
    finalConfig,
    "PUT"
  );
};

export const validateKPIReviews = (kpiReviews: Record<string, any>[]) => {
  for (let i = 0; i < kpiReviews.length; i++) {
    const review = kpiReviews[i];
    let error: Record<string, string> = {};
    const activeKeys = getActiveKeys(review);
    for (let prop in review) {
      const err = validateKPIReviewField(
        review[prop].id,
        review[prop],
        error,
        activeKeys
      );
      const ans = isErrorPresent(err);
      if (ans) {
        return false;
      }
    }
  }
  return true;
};

export const getActiveKeys = (kpiReview: Record<string, any>) => {
  const optionsArr = COMPARISONS_OPERAND_OPTIONS_ID[kpiReview.id];
  if (!optionsArr) return [];
  return optionsArr.filter((id: string) => kpiReview[id].show);
};

const isErrorPresent = (data: any): any => {
  if (typeof data === "string") {
    if (data.trim() === "") return false;
    else return true;
  } else if (typeof data === "object") {
    if (Object.keys(data).length === 0) return false;
    let ans = false;
    for (let prop in data) {
      ans = ans || isErrorPresent(data[prop]);
    }
    return ans;
  } else return false;
};

const getFormattedBody = (KPIReviews: Record<string, any>[]) => {
  const newBody: Record<string, any>[] = [];
  KPIReviews.forEach((review: Record<string, any>) => {
    newBody.push(getBody(review));
  });
  return newBody;
};

const getBody = (review: Record<string, any>): Record<string, any> => {
  if (review.id === "productiveCall") {
    return {
      id: review.id,
      label: review.mainLabel.label,
      tag: { ...review.tag },
      name: review.name,
      main: review.combined,
      totalCall: review.totalCall,
      productiveCall: review.productiveCall,
      daysPassed: review.daysPassed,
      daysWith70perTGTAch: review.daysWith70perTGTAch,
    };
  } else if (review.id === "salesValue") {
    return {
      id: review.id,
      label: review.mainLabel.label,
      tag: { ...review.tag },
      name: review.name,
      main: review.combined,
      prd_tgt: review.prd_tgt,
      ptd_ach: review.ptd_ach,
      qtr_ach_per: review.qtr_ach_per,
      qtr_tgt: review.qtr_tgt,
    };
  } else if (review.id === "mslDistribution") {
    return {
      id: review.id,
      label: review.mainLabel.label,
      tag: { ...review.tag },
      name: review.name,
      main: review.combined,
    };
  } else return {};
};

export const convertBodyToStateFormat = (
  kpiReviews: Record<string, any>[]
): Record<string, any>[] => {
  const body: Record<string, any>[] = [];
  kpiReviews.forEach((review: Record<string, any>) => {
    body.push(getStateFormat(review));
  });

  return body;
};

export const getStateFormat = (review: Record<string, any>) => {
  if (review.id === "salesValue") {
    return {
      id: review.id,
      name: review.name,
      mainLabel: {
        id: "mainLabel",
        label: review.label,
      },
      tag: {
        ...review.tag,
      },
      combined: review.main,
      prd_tgt: review.prd_tgt,
      ptd_ach: review.ptd_ach,
      qtr_ach_per: review.qtr_ach_per,
      qtr_tgt: review.qtr_tgt,
    };
  } else if (review.id === "mslDistribution") {
    return {
      id: review.id,
      name: review.name,
      mainLabel: {
        id: "mainLabel",
        label: review.label,
      },
      tag: {
        ...review.tag,
      },
      combined: review.main,
    };
  } else if (review.id === "productiveCall") {
    return {
      id: review.id,
      name: review.name,
      mainLabel: {
        id: "mainLabel",
        label: review.label,
      },
      tag: {
        ...review.tag,
      },
      combined: review.main,
      totalCall: review.totalCall,
      productiveCall: review.productiveCall,
      daysPassed: review.daysPassed,
      daysWith70perTGTAch: review.daysWith70perTGTAch,
    };
  } else return {};
};

export const getComparisonOptions = (
  kpiReview: Record<string, any>
): { label: string; value: string }[] => {
  const options: { value: string; label: string }[] = [];
  COMPARISONS_OPERAND_OPTIONS_ID[kpiReview.id].map((id: string) => {
    if (kpiReview[id].show)
      options.push({ value: id, label: kpiReview[id]?.label ?? "" });
  });
  return options;
};

export const thresholdValidationResult = (
  thresholdValidation: Record<string, string>,
  valueToCompare: number
) => {
  const { value } = thresholdValidation;
  return Number(valueToCompare) > Number(value)
    ? GREATER
    : Number(valueToCompare) === Number(value)
    ? EQUAL
    : LESS;
};

export const comparisonValidationResult = (
  comparisonValidation: Record<string, string>
) => {
  const { operand1, operand2, operation } = comparisonValidation;
  const fun = COMPARATOR_MAP_FUNCTION[operation];
  const val1 = Number(DEFAULT_PERVIEW_VALUE[operand1].value);
  const val2 = Number(DEFAULT_PERVIEW_VALUE[operand2].value);

  return fun(val1, val2) ? SUCCESS : FAILURE;
};

// export const getColor = (data: any, fieldToCheck: string) => {
//     const validation = data[fieldToCheck].validation;
//     const type = validation.type;
//     if (type === null) return '';

//     const valueToCompare = DEFAULT_PERVIEW_VALUE[fieldToCheck].value;

//     if (type === THRESHOLD.value) {
//         const res = thresholdValidationResult(validation[type], valueToCompare);
//         return res === GREATER ? validation[type].gc : res === LESS ? validation[type].lc : validation[type].ec;
//     } else {
//         const res = comparisonValidationResult(validation.comparison);
//         return res === SUCCESS ? validation[type].sc : validation[type].fc;
//     }
// }

export const getColor = (data: Record<string, any>, comp: any): string => {
  const defaultColor = "#000000"; // Default black color

  if (!data.validation || !data.validation.type) {
    return defaultColor;
  }

  const validationType = data.validation.type;

  if (validationType === "range") {
    const range = data.validation.range;
    if (range) {
      for (const rangeItem of range) {
        const startValue =
          rangeItem.startValue === "min"
            ? -Infinity
            : parseFloat(rangeItem.startValue);
        const endValue =
          rangeItem.endValue === "max"
            ? Infinity
            : parseFloat(rangeItem.endValue);

        if (comp >= startValue && comp <= endValue) {
          return rangeItem.color;
        }
      }
    }
  } else if (validationType === "threshold") {
    const threshold = data.validation.threshold;
    if (threshold) {
      const value = parseFloat(threshold.value);

      if (comp === value) {
        return threshold.ec;
      } else if (comp > value) {
        return threshold.gc;
      } else if (comp < value) {
        return threshold.lc;
      }
    }
  } else if (validationType === "comparison") {
    const comparison = data.validation.comparison;
    if (comparison) {
      for (const comparisonItem of comparison) {
        if (
          comparisonItem.operation !== undefined &&
          comparisonItem.operation !== "" &&
          comparisonItem.operand1 !== "" &&
          comparisonItem.operand2 !== ""
        ) {
          const operand1Value =
            DEFAULT_PERVIEW_VALUE[comparisonItem.operand1]?.value || "11";
          const operand2Value =
            DEFAULT_PERVIEW_VALUE[comparisonItem.operand2]?.value || "11";
          const operation = comparisonItem.operation;
          const expression = `${operand1Value} ${operation} ${operand2Value}`;
          if (eval(expression)) {
            return comparisonItem.color;
          }
        }
      }
    }
  }

  return defaultColor;
};

export const getValidationResult = (
  validation: Record<string, any>,
  valueTocheck: number
) => {
  const { type, threshold } = validation; //comparison removed from here
  if (type === THRESHOLD.value) {
    return thresholdValidationResult(threshold, valueTocheck);
  }
  // else return comparisonValidationResult(comparison);
  else return null;
};

export const validateValidationInput = (key: string, val: string): boolean => {
  const validPattern = /^(\d+(\.\d+)?)$/;
  if (key === "value" || key === "endValue" || key === "startValue") {
    const isValidNumber = validPattern.test(val);
    const isValidString = val === "min" || val === "max";
    return isValidNumber || isValidString;
  } else if (
    key === "color" ||
    key === "ec" ||
    key === "gc" ||
    key === "lc" ||
    key === "fc" ||
    key === "sc"
  ) {
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexColorRegex.test(val);
  } else if (key === "operand1" || key === "operand2" || key === "operation") {
    return val !== "";
  }
  return false;
};

export const getSummaryCardIdOfToggle = (
  cardId: string,
  toggleId: string
): string => {
  const cardInfo: Record<string, any> = TOGGLE_MAP_CARD[cardId];
  if (cardInfo === null || cardInfo === undefined) {
    return "false";
  }
  if (toggleId in cardInfo) {
    return cardInfo[toggleId];
  } else return cardInfo["default"];
};

export const getTableIdOfToggle = (
  cardId: string,
  toggleId: string
): string => {
  const tableInfo: Record<string, any> = TOGGLE_MAP_TABLE[cardId];
  if (tableInfo === null || tableInfo === undefined) {
    return "false";
  }

  if (toggleId in tableInfo) {
    return tableInfo[toggleId];
  } else if ("default" in tableInfo) {
    return tableInfo["default"];
  } else return "false";
};

export function validateSavingData(kpiReview: Record<string, any>): boolean {
  const filter = kpiReview.filter((item: Record<string, any>) => {
    return "data_points" in item || "detail_summary" in item;
  });
  for (const item of filter) {
    if (
      "title" in item &&
      typeof item.title === "string" &&
      item.title.trim() === ""
    ) {
      return false;
    }

    if ("data_points" in item) {
      for (const point of item.data_points) {
        if (
          !validateValidation(point) ||
          ("title" in point &&
            typeof point.title === "string" &&
            point.title.trim() === "")
        ) {
          return false;
        }
      }
    }
    if (item.detail_summary) {
      for (const summary of item.detail_summary) {
        if (summary?.type === "card") {
          if (
            !validateValidation(summary) ||
            ("title" in summary &&
              typeof summary.title === "string" &&
              summary.title.trim() === "")
          ) {
            return false;
          }
        }
        if ("data_points" in summary) {
          for (const point of summary.data_points) {
            if (!validateValidation(point)) {
              return false;
            }
          }
        }
        if ("toggle_list" in summary) {
          for (const point of summary.toggle_list) {
            if (!validateValidation(point)) {
              return false;
            }
          }
        }
      }
      if (item.detail_summary.length === 0) {
        return false;
      }
    }
  }
  return true;
}

function validateValidation(item: Record<string, any>): boolean {
  if ("validation" in item) {
    const validation = item.validation;
    const validationType = validation.type;
    if (validationType === "threshold" && validation.threshold) {
      return validateValidationInputs(validation.threshold);
    } else if (validationType === "range" && validation.range) {
      let result = true;
      validation.range.map((item: Record<string, any>) => {
        result = result && validateValidationInputs(item);
      });
      return result;
    } else if (validationType === "runRate" && validation.runRate) {
      return validateValidationInputs(validation.runRate);
    } else if (validationType === "comparison" && validation.comparison) {
      let result = true;
      validation.comparison.map((item: Record<string, any>) => {
        result = result && validateValidationInputs(item);
      });
      return result;
    }
  } else {
    if (
      "label" in item &&
      typeof item.label === "string" &&
      item.label.trim() === ""
    ) {
      console.error("Label cannot be an empty string");
      return false;
    }
  }

  return true;
}

function validateValidationInputs(validation: Record<string, any>): boolean {
  for (const key in validation) {
    if (!validateValidationInput(key, validation[key])) {
      console.error(`Invalid input for ${key}: ${validation[key]}`);
      return false;
    }
  }

  return true;
}

export const isValidateValue = (value: string): boolean => {
  if (value === "min" || value === "max") {
    return true;
  }

  const numberPattern = /^(\d+(\.\d+)?)$/;
  return numberPattern.test(value);
};

export function enrichDbDataWithDummyData(
  dummyData: Record<string, any>[],
  dbData: Record<string, any>[]
): Record<string, any>[] {
  return gettingDummyData(dummyData, dbData);

  function gettingDummyData(
    dummyData: Record<string, any>[],
    dbData: Record<string, any>[]
  ): Record<string, any>[] {
    const filterDbData = dbData.filter((item: Record<string, any>) => {
      return "data_points" in item || "detail_summary" in item;
    });

    const filteredDummyData = dummyData.filter((item: Record<string, any>) => {
      return filterDbData.some(
        (dbItem: Record<string, any>) => dbItem.id === item.id
      );
    });

    filteredDummyData.forEach((dummyItem: Record<string, any>) => {
      const dbItem = filterDbData.find(
        (dbItem: Record<string, any>) => dbItem.id === dummyItem.id
      );

      if (dbItem) {
        if ("data_points" in dummyItem && !("data_points" in dbItem)) {
          if (!dbItem.data_points) {
            dbItem.data_points = [];
          }
          dbItem.data_points.push(dummyItem.data_points);
        }
        if ("data_points" in dummyItem && "data_points" in dbItem) {
          let missingDataPoints = dummyItem.data_points.filter(
            (dummyPoint: Record<string, any>) =>
              !dbItem.data_points.some(
                (dbItemPoint: Record<string, any>) =>
                  dbItemPoint.id === dummyPoint.id
              )
          );
          if (missingDataPoints.length > 0) {
            missingDataPoints = missingDataPoints.filter(
              (missingPoint: Record<string, any>) => {
                missingPoint.show = false;
                return true;
              }
            );
            dbItem.data_points = [
              ...(dbItem?.data_points || []),
              ...missingDataPoints,
            ];
          }
        }

        if ("detail_summary" in dummyItem && !("detail_summary" in dbItem)) {
          if (!dbItem.detail_summary) {
            dbItem.detail_summary = [];
          }
          dbItem.detail_summary.push(dummyItem.detail_summary);
        }

        if ("detail_summary" in dummyItem && "detail_summary" in dbItem) {
          let missingDetailSummary = dummyItem.detail_summary.filter(
            (dummySummary: Record<string, any>) =>
              !dbItem.detail_summary.some(
                (dbItemSummary: Record<string, any>) =>
                  dbItemSummary.id === dummySummary.id
              )
          );
          if (missingDetailSummary.length > 0) {
            missingDetailSummary = missingDetailSummary.filter(
              (missingSummary: Record<string, any>) => {
                missingSummary.show = false;
                return true;
              }
            );
            dbItem.detail_summary = [
              ...(dbItem?.detail_summary || []),
              ...missingDetailSummary,
            ];
          }

          dummyItem.detail_summary.forEach(
            (dummySummary: Record<string, any>) => {
              const dbSummary = dbItem?.detail_summary?.find(
                (dbSummary: Record<string, any>) =>
                  dbSummary.id === dummySummary.id
              );

              if ("toggle_list" in dummySummary && "toggle_list" in dbSummary) {
                let missingDetailSummaryToggleList =
                  dummySummary.toggle_list.filter(
                    (dummyToggleList: Record<string, any>) =>
                      !dbSummary.toggle_list.some(
                        (dbLoggleList: Record<string, any>) =>
                          dbLoggleList.id === dummyToggleList.id
                      )
                  );
                if (missingDetailSummaryToggleList.length > 0) {
                  missingDetailSummaryToggleList =
                    missingDetailSummaryToggleList.filter(
                      (missingToogle: Record<string, any>) => {
                        missingToogle.show = false;
                        return true;
                      }
                    );
                  dbSummary.toggle_list = [
                    ...(dbSummary?.toggle_list || []),
                    ...missingDetailSummaryToggleList,
                  ];
                }
              }

              if (
                "toggle_list" in dummySummary &&
                !("toggle_list" in dbSummary)
              ) {
                if (!dbSummary.toggle_list) {
                  dbSummary.toggle_list = [];
                }
                dbSummary.toggle_list.push(dummySummary.toggle_list);
              }

              if (
                "data_points" in dummySummary &&
                !("data_points" in dbSummary)
              ) {
                if (!dbSummary.data_points) {
                  dbSummary.data_points = [];
                }
                dbSummary.data_points.push(dummySummary.data_points);
              }
              if ("data_points" in dummySummary && "data_points" in dbSummary) {
                let missingDataPoints = dummySummary.data_points.filter(
                  (dummyPoint: Record<string, any>) =>
                    !dbSummary.data_points.some(
                      (dbSummaryPoint: Record<string, any>) =>
                        dbSummaryPoint.id === dummyPoint.id
                    )
                );
                if (missingDataPoints.length > 0) {
                  missingDataPoints = missingDataPoints.filter(
                    (missingPoint: Record<string, any>) => {
                      missingPoint.show = false;
                      return true;
                    }
                  );
                  dbSummary.data_points = [
                    ...(dbSummary?.data_points || []),
                    ...missingDataPoints,
                  ];
                }
              }
            }
          );
        }
      }
    });
    // console.log("dbData -> ", dbData);
    return dbData;
  }
}

export function getEmoteForValidation(
  data: Record<string, any>,
  comp: any
): string {
  if (!data.validation || !data.validation?.type) {
    return "";
  }

  const validationType = data.validation.type;
  if (validationType === "range") {
    const range = data.validation.range;
    if (range) {
      for (const rangeItem of range) {
        const startValue =
          rangeItem.startValue === "min"
            ? -Infinity
            : parseFloat(rangeItem.startValue);
        const endValue =
          rangeItem.endValue === "max"
            ? Infinity
            : parseFloat(rangeItem.endValue);

        if (comp >= startValue && comp <= endValue) {
          return "happy";
        }
      }
      return "neutral";
    }
  } else if (validationType == "threshold") {
    const threshold = data.validation.threshold;
    if (threshold) {
      const value = parseFloat(threshold.value);
      if (comp === value) {
        return "neutral";
      } else if (comp > value) {
        return "happy";
      } else if (comp < value) {
        return "neutral";
      }
    }
  } else if (validationType === "comparison") {
    const comparison = data.validation.comparison;
    if (comparison) {
      for (const comparisonItem of comparison) {
        if (
          comparisonItem.operation !== undefined &&
          comparisonItem.operation !== "" &&
          comparisonItem.operand1 !== "" &&
          comparisonItem.operand2 !== ""
        ) {
          const operand1Value =
            DEFAULT_PERVIEW_VALUE[comparisonItem.operand1]?.value || "11";
          const operand2Value =
            DEFAULT_PERVIEW_VALUE[comparisonItem.operand2]?.value || "11";
          const operation = comparisonItem.operation;
          const expression = `${operand1Value} ${operation} ${operand2Value}`;
          if (eval(expression)) {
            return comparisonItem.emoji;
          }
        }
      }
    }
  }

  return "";
}

export function getMissingFields(kpiReview: Record<string, any>) {
  let err: Record<string, any>[] = [];
  let cardsError: Record<string, any> = {};
  const filter = kpiReview.filter((item: Record<string, any>) => {
    return "data_points" in item || "detail_summary" in item;
  });
  for (const item of filter) {
    cardsError = {};
    if (
      "title" in item &&
      typeof item.title === "string" &&
      item.title.trim() === ""
    ) {
      cardsError[ID_NAME_MAP[item.id]] = "Field is required";
    }

    if ("data_points" in item) {
      for (const point of item.data_points) {
        validationCheck(point, cardsError);
        if (
          "title" in point &&
          typeof point.title === "string" &&
          point.title.trim() === ""
        ) {
          cardsError[ID_NAME_MAP[point.id]] = "Field is required";
        }
      }
    }
    if (item.detail_summary) {
      for (const summary of item.detail_summary) {
        if (summary?.type === "card") {
          validationCheck(summary, cardsError);
          if (
            "title" in summary &&
            typeof summary.title === "string" &&
            summary.title.trim() === ""
          ) {
            cardsError[ID_NAME_MAP[summary.id]] = "Field is required";
          }
        }
        if ("data_points" in summary) {
          for (const point of summary.data_points) {
            validationCheck(point, cardsError);
          }
        }
        if ("toggle_list" in summary) {
          for (const point of summary.toggle_list) {
            validationCheck(point, cardsError);
          }
        }
      }
    }
    if (Object.keys(cardsError).length !== 0) {
      cardsError.cardName = item.id
        .replace(/([A-Z])/g, " $1")
        .replace(/^\w/, (c: string) => c.toUpperCase());
      err = [...err, cardsError];
    }
  }
  return err;
}

function validationCheck(
  item: Record<string, any>,
  err: Record<string, any>
): boolean {
  if ("validation" in item) {
    const validation = item.validation;
    const validationType = validation.type;
    if (validationType === "threshold" && validation.threshold) {
      return validateInputs(validation.threshold, err);
    } else if (validationType === "range" && validation.range) {
      let result = true;
      validation.range.map((item: Record<string, any>) => {
        result = result && validateInputs(item, err);
      });
      return result;
    } else if (validationType === "runRate" && validation.runRate) {
      return validateInputs(validation.runRate, err);
    } else if (validationType === "comparison" && validation.comparison) {
      let result = true;
      validation.comparison.map((item: Record<string, any>) => {
        result = result && validateInputs(item, err);
      });
      return result;
    }
  } else {
    if (
      "label" in item &&
      typeof item.label === "string" &&
      item.label.trim() === ""
    ) {
      err[ID_NAME_MAP[item.id]] = "Field is required";
      return false;
    }
  }

  return true;
}

function validateInputs(
  validation: Record<string, any>,
  err: Record<string, any>
): boolean {
  for (const key in validation) {
    if (!validateValidationInput(key, validation[key])) {
      err[key] = "Field is required";
      return false;
    }
  }

  return true;
}
